import {
  InvalidDataTypesDetails,
  InvalidDataTypes,
} from "@/components/PImport/types/InvalidItem";
import { isValidUrlFieldValue } from "./urlField";
import { parseImportBoolean } from "./import-boolean-words";
import {
  cleanEmail,
  emailRuleOffender,
  emailRuleReason,
  isDeliverableEmail,
} from "@/lib/email-rule";

/**
 * Module-level helper (not a class method) so it works even when the
 * detector probes are called with a detached `this` (the test suite does this
 * by indexing into the class as a record).
 */
/** A cell on one line: line breaks to single spaces, like `normalizeLineBreaks`. */
function foldLineBreaks(value: string): string {
  return value.replace(/\r\n|\r|\n/g, " ").replace(/\s+/g, " ").trim();
}

function isEmptyCell(val: unknown): val is null | "" {
  return (
    val === null ||
    val === undefined ||
    (typeof val === "string" && val.trim() === "")
  );
}

/**
 * Service for detecting data types from string arrays (like those imported from Excel or CSV)
 */
export class DataTypeDetectionService {
  // Regular expression patterns for validation
  private static readonly INVALID_STRING_PATTERN =
    /[\u{1F000}-\u{1F9FF}\u{2600}-\u{27BF}\u0000-\u001F\u007F-\u009F]/u;
  private static readonly VALID_LONG_PATTERN = /^-?\d+$/;
  private static readonly MAX_LONG_LENGTH = 20;
  private static readonly VALID_DOUBLE_PATTERN = /^-?\d*\.?\d*$/;
  private static readonly MAX_DOUBLE_LENGTH = 32;
  // E-mail: the ONE rule of the app (`src/lib/email-rule.ts` — the edit
  // cell's, the server's and the consumer's), not a private copy. This
  // detector also validates values on the way OUT — `parseEmail` in parser.ts
  // runs every import row (and the import mapper's default cell) through it —
  // so a copy that drifted from the edit cell's pattern accepted an address in
  // the mapper box and dropped it from the row. That happened. The rule's
  // cleaning runs first (`cleanEmailValue`): a cell that is deliverable once
  // its invisible characters and edge whitespace are gone is valid, and the
  // clean value is what the import hands on.
  // BOOLEAN words: not a list here. The import reads them from ONE data file
  // (import-boolean-words.json, via parseImportBoolean) — today's
  // true/false/yes/no/1/0/y/n plus the Yes/No of every cockpit language, so
  // a "nein" opt-in cell is a NO and not an empty cell (⚑14).
  private static readonly DATE_FORMATS = [
    // ISO Date format
    /^\d{4}-\d{2}-\d{2}$/,
    // European format
    /^\d{2}\.\d{2}\.\d{4}$/,
    // US format
    /^\d{2}\/\d{2}\/\d{4}$/,
    // Other common formats
    /^\d{1,2}\-\d{1,2}\-\d{4}$/,
  ];
  private static readonly DATETIME_FORMATS = [
    // ISO datetime with T separator
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/,
    // ISO datetime with space separator
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?(\.\d+)?$/,
    // European datetime
    /^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}(:\d{2})?$/,
    // US datetime
    /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}(:\d{2})?$/,
  ];
  private static readonly OBJECTID_FORMATS = /^[0-9a-fA-F]{24}$/;

  /**
   * Helper function to find the first invalid character by testing character removal
   */
  private static findFirstInvalidCharByRemoval(
    value: string,
    validationPattern: RegExp
  ): string | null {
    // Test removing each character to see which one is causing the validation to fail
    for (let i = 0; i < value.length; i++) {
      const charToTest = value[i];
      const valueWithoutChar = value.slice(0, i) + value.slice(i + 1);

      // If removing this character makes the remaining string valid, this char is the problem
      if (validationPattern.test(valueWithoutChar)) {
        return charToTest;
      }
    }

    // If no single character removal fixes it, return the first non-matching character
    // based on the pattern structure
    return this.findFirstObviouslyInvalidChar(value, validationPattern);
  }

  /**
   * Helper function to find obviously invalid characters based on pattern analysis
   */
  private static findFirstObviouslyInvalidChar(
    value: string,
    validationPattern: RegExp
  ): string | null {
    // For long pattern (digits and minus): find first non-digit, non-minus
    if (validationPattern === this.VALID_LONG_PATTERN) {
      for (let i = 0; i < value.length; i++) {
        const char = value[i];
        if (!/[-\d]/.test(char)) {
          return char;
        }
      }
    }

    // For double pattern (digits, minus, dot): find first invalid char
    if (validationPattern === this.VALID_DOUBLE_PATTERN) {
      for (let i = 0; i < value.length; i++) {
        const char = value[i];
        if (!/[-\d.]/.test(char)) {
          return char;
        }
      }
    }

    return null;
  }

  /**
   * Helper function to find the first invalid character for long values
   */
  private static findFirstInvalidCharForLong(value: string): string | null {
    return this.findFirstInvalidCharByRemoval(value, this.VALID_LONG_PATTERN);
  }

  /**
   * Helper function to find the first invalid character for double values
   */
  private static findFirstInvalidCharForDouble(value: string): string | null {
    return this.findFirstInvalidCharByRemoval(value, this.VALID_DOUBLE_PATTERN);
  }

  /**
   * Helper function to find the first invalid character for email values
   */
  private static findFirstInvalidCharForEmail(value: string): string | null {
    // `value` is the CLEANED cell. The hint names the character the rule's
    // first reason points at — inner whitespace, a non-ASCII letter before
    // the "@" or in the domain (`emailRuleOffender`); a shape slip (no "@",
    // two "@", no dot in the domain) is not one character's fault.
    const reason = emailRuleReason(value);
    if (reason === null) return null;
    return emailRuleOffender(value, reason)?.char ?? null;
  }

  /**
   * Helper function to find the first invalid character for ObjectId values
   */
  private static findFirstInvalidCharForObjectId(value: string): string | null {
    return this.findFirstInvalidCharByRemoval(value, this.OBJECTID_FORMATS);
  }

  /**
   * Helper function to find the first invalid character using the invalid string pattern
   */
  private static findFirstInvalidCharForString(value: string): string | null {
    const match = value.match(this.INVALID_STRING_PATTERN);
    return match ? match[0] : null;
  }

  /**
   * Normalizes string values by replacing line breaks with spaces
   * Handles various line break types: \n, \r\n, \r
   * @param value The string value to normalize
   * @returns Normalized string with line breaks replaced by spaces
   */
  private static normalizeLineBreaks(value: string): string {
    if (!value || typeof value !== "string") {
      return value;
    }

    // Replace all types of line breaks with single spaces
    // \r\n (Windows), \n (Unix/Linux), \r (old Mac)
    return value
      .replace(/\r\n|\r|\n/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  /**
   * Get all possible data types for a column of values
   * @param values Array of string values to analyze
   * @returns Array of possible DataType enum values in order of specificity
   */
  public static getDataTypes(
    values: (string | null | undefined)[]
  ): InvalidDataTypes {
    // Initialize result object
    const invalidDataTypes: InvalidDataTypes = {
      long: null,
      double: null,
      boolean: null,
      email: null,
      url: null,
      date: null,
      dateTime: null,
      string: null,
      objectId: null,
    };

    // Safety check for null/undefined input
    if (!values || !Array.isArray(values)) {
      return invalidDataTypes;
    }

    // Normalize line breaks but PRESERVE positions — detectors return an
    // index that the UI surfaces as an Excel row number, so the index must
    // match the original `values` array, not a filtered subset.
    const normalized: (string | null)[] = values.map((val) =>
      val === null || val === undefined
        ? null
        : this.normalizeLineBreaks(val as string),
    );

    // If every cell is empty, no need to run detectors
    if (normalized.every((v) => v === null || v === "")) {
      return invalidDataTypes;
    }

    const isLong = this.isLong(normalized);
    const isDouble = this.isDouble(normalized);
    const isBoolean = this.isBoolean(normalized);
    // E-mail is judged on the RAW cells: the rule does its own cleaning, and
    // a line break folded to a space above would be reported as "U+0020" on
    // a cell that contains no space.
    const isEmail = this.isEmail(
      values.map((val) => (val === null || val === undefined ? null : val)),
    );
    const isUrl = this.isUrl(normalized);
    const isDate = this.isDate(normalized);
    const isDateTime = this.isDateTime(normalized);
    const isValidString = this.isValidString(normalized);
    const isValidObjectId = this.isValidObjectId(normalized);

    // Check each data type
    if (isLong) invalidDataTypes.long = isLong;
    if (isDouble) invalidDataTypes.double = isDouble;
    if (isBoolean) invalidDataTypes.boolean = isBoolean;
    if (isEmail) invalidDataTypes.email = isEmail;
    if (isUrl) invalidDataTypes.url = isUrl;
    if (isDate) invalidDataTypes.date = isDate;
    if (isDateTime) invalidDataTypes.dateTime = isDateTime;
    if (isValidString) invalidDataTypes.string = isValidString;
    if (isValidObjectId) invalidDataTypes.objectId = isValidObjectId;

    return invalidDataTypes;
  }

  /**
   * Determines if all values are valid strings (don't contain invalid characters)
   * @returns null if valid, or details about the first invalid value found
   */
  private static isValidString(
    values: (string | null)[]
  ): InvalidDataTypesDetails | null {
    if (!values || !Array.isArray(values)) return null;
    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      if (isEmptyCell(val)) continue;
      if (typeof val !== "string") continue;
      if (this.INVALID_STRING_PATTERN.test(val)) {
        return {
          index: i,
          value: val,
          invalidChar: this.findFirstInvalidCharForString(val),
        };
      }
    }
    return null;
  }

  private static isValidObjectId(
    values: (string | null)[]
  ): InvalidDataTypesDetails | null {
    if (!values || !Array.isArray(values)) return null;
    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      if (isEmptyCell(val)) continue;
      if (typeof val !== "string") {
        return { index: i, value: String(val), invalidChar: null };
      }
      if (!this.OBJECTID_FORMATS.test(val)) {
        return {
          index: i,
          value: val,
          invalidChar: this.findFirstInvalidCharForObjectId(val),
        };
      }
    }
    return null;
  }

  private static isBoolean(
    values: (string | null)[]
  ): InvalidDataTypesDetails | null {
    if (!values || !Array.isArray(values)) return null;
    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      if (isEmptyCell(val)) continue;
      if (typeof val !== "string") {
        return { index: i, value: String(val), invalidChar: null };
      }
      if (parseImportBoolean(val) === null) {
        return { index: i, value: val, invalidChar: null };
      }
    }
    return null;
  }

  private static isLong(
    values: (string | null)[]
  ): InvalidDataTypesDetails | null {
    if (!values || !Array.isArray(values)) return null;
    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      if (isEmptyCell(val)) continue;
      if (typeof val !== "string") {
        return { index: i, value: String(val), invalidChar: null };
      }
      const trimmed = val.trim();
      if (trimmed.length > this.MAX_LONG_LENGTH) {
        return { index: i, value: val, invalidChar: null };
      }
      if (!this.VALID_LONG_PATTERN.test(trimmed)) {
        return {
          index: i,
          value: val,
          invalidChar: this.findFirstInvalidCharForLong(trimmed),
        };
      }
    }
    return null;
  }

  private static isDouble(
    values: (string | null)[]
  ): InvalidDataTypesDetails | null {
    if (!values || !Array.isArray(values)) return null;
    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      if (isEmptyCell(val)) continue;
      if (typeof val !== "string") {
        return { index: i, value: String(val), invalidChar: null };
      }
      const trimmed = val.trim();
      if (trimmed.length > this.MAX_DOUBLE_LENGTH) {
        return { index: i, value: val, invalidChar: null };
      }
      if (!this.VALID_DOUBLE_PATTERN.test(trimmed)) {
        return {
          index: i,
          value: val,
          invalidChar: this.findFirstInvalidCharForDouble(trimmed),
        };
      }
    }
    return null;
  }

  private static isEmail(
    values: (string | null)[]
  ): InvalidDataTypesDetails | null {
    if (!values || !Array.isArray(values)) return null;
    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      if (isEmptyCell(val)) continue;
      if (typeof val !== "string") {
        return { index: i, value: String(val), invalidChar: null };
      }
      const cleaned = cleanEmail(val);
      // Nothing left once cleaned (a cell that held only a zero-width space,
      // a BOM): an empty cell, not an undeliverable address.
      if (cleaned === "") continue;
      if (!isDeliverableEmail(cleaned)) {
        return {
          index: i,
          // Shown in the mapper's tooltip: on one line, as for every column.
          value: foldLineBreaks(val),
          invalidChar: this.findFirstInvalidCharForEmail(cleaned),
        };
      }
    }
    return null;
  }

  private static isUrl(
    values: (string | null)[]
  ): InvalidDataTypesDetails | null {
    if (!values || !Array.isArray(values)) return null;
    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      if (isEmptyCell(val)) continue;
      if (typeof val !== "string" || !isValidUrlFieldValue(val.trim())) {
        return { index: i, value: String(val), invalidChar: null };
      }
    }
    return null;
  }

  private static isDate(
    values: (string | null)[]
  ): InvalidDataTypesDetails | null {
    if (!values || !Array.isArray(values)) return null;
    const isValidDateString = (value: string): boolean => {
      const matchesPattern = this.DATE_FORMATS.some((p) => p.test(value));
      if (!matchesPattern) return false;
      try {
        return !isNaN(new Date(value).getTime());
      } catch {
        return false;
      }
    };
    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      if (isEmptyCell(val)) continue;
      if (typeof val !== "string") {
        return { index: i, value: String(val), invalidChar: null };
      }
      const trimmed = val.trim();
      const hasTimeComponent = this.DATETIME_FORMATS.some((p) =>
        p.test(trimmed),
      );
      if (hasTimeComponent || !isValidDateString(trimmed)) {
        return { index: i, value: val, invalidChar: null };
      }
    }
    return null;
  }

  private static isDateTime(
    values: (string | null)[]
  ): InvalidDataTypesDetails | null {
    if (!values || !Array.isArray(values)) return null;
    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      if (isEmptyCell(val)) continue;
      if (typeof val !== "string") {
        return { index: i, value: String(val), invalidChar: null };
      }
      const trimmed = val.trim();
      const matchesPattern = this.DATETIME_FORMATS.some((p) => p.test(trimmed));
      if (!matchesPattern) {
        return { index: i, value: val, invalidChar: null };
      }
      try {
        if (isNaN(new Date(trimmed).getTime())) {
          return { index: i, value: val, invalidChar: null };
        }
      } catch {
        return { index: i, value: val, invalidChar: null };
      }
    }
    return null;
  }

  static isValidStringValue(value: string | null | undefined): boolean {
    if (value === null || value === undefined || typeof value !== "string") {
      return false;
    }

    const normalizedValue = this.normalizeLineBreaks(value);
    return !this.INVALID_STRING_PATTERN.test(normalizedValue);
  }

  static isValidObjectIdValue(value: string | null | undefined): boolean {
    if (value === null || value === undefined || typeof value !== "string") {
      return false;
    }

    const normalizedValue = this.normalizeLineBreaks(value);
    return this.OBJECTID_FORMATS.test(normalizedValue);
  }

  static isValidBooleanValue(value: string | null | undefined): boolean {
    if (value === null || value === undefined || typeof value !== "string") {
      return false;
    }

    return parseImportBoolean(value) !== null;
  }

  static isValidLongValue(value: string | null | undefined): boolean {
    if (value === null || value === undefined || typeof value !== "string") {
      return false;
    }

    const normalizedValue = this.normalizeLineBreaks(value);
    const trimmed = normalizedValue.trim();
    return (
      this.VALID_LONG_PATTERN.test(trimmed) &&
      trimmed.length <= this.MAX_LONG_LENGTH
    );
  }

  static isValidDoubleValue(value: string | null | undefined): boolean {
    if (value === null || value === undefined || typeof value !== "string") {
      return false;
    }

    const normalizedValue = this.normalizeLineBreaks(value);
    const trimmed = normalizedValue.trim();
    return (
      this.VALID_DOUBLE_PATTERN.test(trimmed) &&
      trimmed.length <= this.MAX_DOUBLE_LENGTH
    );
  }

  /**
   * The e-mail value the import hands on: the rule's cleaning and nothing
   * else — invisible code points dropped anywhere, whitespace (line breaks
   * included) off the edges. Inner whitespace stays (and fails).
   *
   * NOT folded through `normalizeLineBreaks` first, as the other columns
   * are: that turns an inner line break into a space (the hint then blamed
   * "U+0020" on a cell without one) and, because `\s` knows U+FEFF, a
   * byte-order mark inside an address into a space too — a healable cell
   * became unhealable. For every cell that IS deliverable the result is the
   * same either way.
   */
  static cleanEmailValue(value: string): string {
    return cleanEmail(value);
  }

  /**
   * An e-mail cell with nothing in it once cleaned: null, blank, or only
   * invisible characters. `"".trim()` does not know U+200B, so such a cell
   * used to count as "an address the rule refuses".
   */
  static isEmptyEmailCell(value: string | null | undefined): boolean {
    return typeof value !== "string" || cleanEmail(value) === "";
  }

  static isValidEmailValue(value: string | null | undefined): boolean {
    if (value === null || value === undefined || typeof value !== "string") {
      return false;
    }
    return isDeliverableEmail(this.cleanEmailValue(value));
  }

  static isValidUrlValue(value: string | null | undefined): boolean {
    if (typeof value !== "string") return false;
    return isValidUrlFieldValue(this.normalizeLineBreaks(value).trim());
  }

  static isValidDateValue(value: string | null | undefined): boolean {
    if (value === null || value === undefined || typeof value !== "string") {
      return false;
    }

    try {
      const normalizedValue = this.normalizeLineBreaks(value);
      const date = new Date(normalizedValue);
      return !isNaN(date.getTime());
    } catch {
      return false;
    }
  }

  /**
   * Per-cell DATE validity that mirrors the {@link isDate} column detector
   * exactly: the value must match one of {@link DATE_FORMATS}, be a real date,
   * and carry no time component. {@link isValidDateValue} is intentionally
   * looser (bare `new Date()` parse) and is therefore NOT interchangeable here
   * — use this method when results must agree with the import warning UI.
   */
  static isValidDateCell(value: string | null | undefined): boolean {
    if (value === null || value === undefined || typeof value !== "string") {
      return false;
    }
    const trimmed = this.normalizeLineBreaks(value).trim();
    // A value carrying a time component is not a valid bare DATE.
    if (this.DATETIME_FORMATS.some((p) => p.test(trimmed))) return false;
    if (!this.DATE_FORMATS.some((p) => p.test(trimmed))) return false;
    try {
      return !isNaN(new Date(trimmed).getTime());
    } catch {
      return false;
    }
  }

  static isValidDateTimeValue(value: string | null | undefined): boolean {
    if (value === null || value === undefined || typeof value !== "string") {
      return false;
    }

    const normalizedValue = this.normalizeLineBreaks(value);
    const trimmed = normalizedValue.trim();

    // Check if string matches any of our datetime patterns
    const matchesPattern = this.DATETIME_FORMATS.some((pattern) =>
      pattern.test(trimmed)
    );

    if (!matchesPattern) return false;

    // Try to create a date object to validate the datetime is real
    try {
      const date = new Date(trimmed);
      return !isNaN(date.getTime());
    } catch {
      return false;
    }
  }
}
